import { forwardRef, type ComponentPropsWithRef } from "react";
import { TableVirtuoso, Virtuoso, type TableComponents } from "react-virtuoso";
import type { Opportunity, OpportunitySubcategory } from "@api/opportunity";
import type { ColumnDef } from "./columnConfig";
import { useIsMobile } from "@/hooks/useResponsive";
import { OpportunityRow } from "./OpportunityRow";
import { AddOpportunityRow } from "./AddOpportunityRow";
import { OpportunityCard } from "./OpportunityCard";
import styles from "./styles.module.css";

type Props = {
  opportunities: Opportunity[];
  isAdmin: boolean;
  isAddingRow: boolean;
  onCancelAdd: () => void;
  selectedCategoryId: string;
  columns: ColumnDef[];
  categoryName: string;
  selectedSubcategory: string | null;
  subcategories: OpportunitySubcategory[];
};

// Static table/tbody wrappers for TableVirtuoso. Defined once (not per-render) so the virtualized list
// keeps its identity; `table-layout: fixed` + per-<th> widths in the header drive the columns, so no
// dynamic colgroup is needed here.
const TableComponent = forwardRef<HTMLTableElement, ComponentPropsWithRef<"table">>(
  ({ style, ...props }, ref) => (
    <table ref={ref} {...props} style={style} aria-label="Opportunities table" className={styles.table} />
  )
);
TableComponent.displayName = "OpportunityVirtuosoTable";

const TableBodyComponent = forwardRef<HTMLTableSectionElement, ComponentPropsWithRef<"tbody">>(
  (props, ref) => <tbody {...props} ref={ref} />
);
TableBodyComponent.displayName = "OpportunityVirtuosoTableBody";

const tableComponents: TableComponents<Opportunity> = {
  Table: TableComponent,
  TableBody: TableBodyComponent,
};

export const OpportunityTable = ({
  opportunities,
  isAdmin,
  isAddingRow,
  onCancelAdd,
  selectedCategoryId,
  columns,
  categoryName,
  selectedSubcategory,
  subcategories,
}: Props) => {
  const isMobile = useIsMobile();
  // Actions column shows when editing is possible: admins always; non-admins when the category has an
  // Open-Edit column (OpportunityRow then restricts them to those cells and hides delete).
  const canEdit = isAdmin || columns.some((c) => c.openEdit);

  // Virtualize both paths: a single subcategory can hold thousands of rows (Pollstar-derived), and
  // mounting them all freezes the main thread. Keying by opp.id is required — without it virtuoso
  // recycles row instances by index and an editing row's state would bleed onto a different opportunity.
  if (isMobile) {
    return (
      <Virtuoso
        style={{ height: "100%" }}
        data={opportunities}
        overscan={200}
        computeItemKey={(_, opp) => opp.id}
        itemContent={(_, opp) => (
          <div className={styles.cardItem}>
            <OpportunityCard
              opportunity={opp}
              isAdmin={isAdmin}
              columns={columns}
              categoryName={categoryName}
              subcategories={subcategories}
            />
          </div>
        )}
      />
    );
  }

  return (
    <TableVirtuoso<Opportunity>
      style={{ height: "100%" }}
      data={opportunities}
      overscan={200}
      computeItemKey={(_, opp) => opp.id}
      components={tableComponents}
      fixedHeaderContent={() => (
        <>
          <tr>
            {columns.map((col) => (
              <th key={col.field} style={{ width: col.width }}>
                {col.header}
              </th>
            ))}
            {canEdit && <th style={{ width: "7%" }}>Actions</th>}
          </tr>
          {isAddingRow && (
            <AddOpportunityRow
              categoryId={selectedCategoryId}
              onCancel={onCancelAdd}
              columns={columns}
              categoryName={categoryName}
              defaultSubcategory={selectedSubcategory}
              subcategories={subcategories}
            />
          )}
        </>
      )}
      itemContent={(_, opp) => (
        <OpportunityRow
          opportunity={opp}
          isAdmin={isAdmin}
          columns={columns}
          subcategories={subcategories}
        />
      )}
    />
  );
};
